import { Alert, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import {
  CardContainer,
  OptedInDocumentTypesSection,
  OrganisationCodeSelector,
  WorkflowSection,
  ReturnPathSection,
  RedirectAllowListSection,
} from '@verifime/components';
import { withClarityTheme } from '@verifime/styles';
import { useState } from 'react';
import { TNotificationHandler } from '../../../types';
import { useOrganisationCodeSettings } from './hooks/useOrganisationCodeSettings';

export interface OrganisationCodeSettingsProps {
  organisationId: string;
  successNotificationHandler?: TNotificationHandler;
  errorNotificationHandler?: TNotificationHandler;
  noCodesMessage?: React.ReactNode;
  /**
   * Shows the opted-in document types section. Admin only: only the admin portal sets this, so
   * any other host (the client portal) leaves the section out.
   */
  showOptedInDocumentTypes?: boolean;
}

// Wrapped in withClarityTheme so the whole card renders under the Clarity design
// system, the target theme for new components. Clarity's subtitle1 is already
// semibold, so the section-title fontWeight overrides are dropped as redundant.
function OrganisationCodeSettings({
  organisationId,
  successNotificationHandler,
  errorNotificationHandler,
  noCodesMessage = 'No organisation codes available. This may indicate a data issue. Please contact the development team for assistance.',
  showOptedInDocumentTypes = false,
}: Readonly<OrganisationCodeSettingsProps>) {
  const {
    selectedCode,
    setSelectedCode,
    isLoading,
    organisationCodes,
    selectedWorkflow,
    disabledWorkflows,
    handleWorkflowSwitch,
    initialReturnUrl,
    handleReturnUrlSave,
    initialRedirectAllowedOrigins,
    handleAllowedOriginsSave,
    availableOptedInDocumentTypes,
    initialOptedInDocumentTypes,
    handleOptedInDocumentTypesSave,
  } = useOrganisationCodeSettings(
    organisationId,
    successNotificationHandler,
    errorNotificationHandler,
  );

  const [isSavingWorkflow, setIsSavingWorkflow] = useState(false);

  const handleWorkflowChange = async (workflow: typeof selectedWorkflow) => {
    setIsSavingWorkflow(true);
    try {
      await handleWorkflowSwitch(workflow);
    } finally {
      setIsSavingWorkflow(false);
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (organisationCodes.length === 0) {
    return (
      <CardContainer elevation={2}>
        <Typography
          variant="h6"
          sx={(theme) => ({ marginBottom: theme.semantic.spacing.base })}
        >
          Organisation Code Settings
        </Typography>
        <Alert severity="error">{noCodesMessage}</Alert>
      </CardContainer>
    );
  }

  return (
    <CardContainer
      elevation={2}
      sx={(theme) => ({
        padding: theme.semantic.spacing.base,
        borderRadius: theme.semantic.borderRadius.lg,
      })}
    >
      <Typography
        variant="h6"
        sx={(theme) => ({ marginBottom: theme.semantic.spacing.base })}
      >
        Organisation Code Settings
      </Typography>

      <OrganisationCodeSelector
        codes={organisationCodes}
        selectedCode={selectedCode}
        onCodeChange={setSelectedCode}
        dataTestId="organisation-code-settings-selector"
      />

      <Stack sx={(theme) => ({ gap: theme.semantic.spacing.lg })}>
        <WorkflowSection
          selectedWorkflow={selectedWorkflow}
          onWorkflowChange={handleWorkflowChange}
          isSaving={isSavingWorkflow}
          disabledWorkflows={disabledWorkflows}
        />

        <Divider />

        <ReturnPathSection
          initialReturnUrl={initialReturnUrl}
          selectedCode={selectedCode}
          onReturnUrlSave={handleReturnUrlSave}
        />

        <Divider />

        <RedirectAllowListSection
          initialAllowedOrigins={initialRedirectAllowedOrigins}
          selectedCode={selectedCode}
          onAllowedOriginsSave={handleAllowedOriginsSave}
        />

        {showOptedInDocumentTypes && (
          <>
            <Divider />

            {/* Keyed by code so the Edit step and a save in progress for one code never show on another. */}
            <OptedInDocumentTypesSection
              key={selectedCode}
              availableDocumentTypes={availableOptedInDocumentTypes}
              selectedDocumentTypes={initialOptedInDocumentTypes}
              onDocumentTypesSave={handleOptedInDocumentTypesSave}
            />
          </>
        )}
      </Stack>
    </CardContainer>
  );
}

export default withClarityTheme(OrganisationCodeSettings);
